/**
 * forecastBackoff.ts — per-host pause after the forecast provider reports a
 * request limit (v56 Task 8).
 *
 * When Open-Meteo answers 429 (Too Many Requests) the app stops touching
 * that host until the pause expires. The pause is persisted per host in
 * local storage so a page refresh does not immediately hammer the provider
 * again. While a host is blocked, fetch callers either serve saved
 * same-cell fallback or surface a clear blocked error — never a network
 * request.
 *
 * Timing rules (in priority order):
 *   1. Retry-After header, when present;
 *   2. the reason phrase: minute limit → 2 min; hourly → 5 min after the
 *      next hour; daily → 5 min after the next UTC midnight;
 *   3. any other block response → 15 minutes.
 */

export const MINUTE_LIMIT_PAUSE_MS = 2 * 60_000;
export const HOURLY_LIMIT_SLACK_MS = 5 * 60_000;
export const DAILY_LIMIT_SLACK_MS = 5 * 60_000;
export const UNKNOWN_BLOCK_PAUSE_MS = 15 * 60_000;

export type LimitKind = 'minute' | 'hourly' | 'daily' | 'unknown';

const STORAGE_PREFIX = 'pps:forecastBackoff:';

/**
 * In-memory fallback for environments without local storage (node tests,
 * sandboxed contexts where storage access throws). When local storage
 * exists it is the durable source of truth; this map only backs it up.
 */
const _memory = new Map<string, number>();

function storage(): Storage | null {
  try {
    if (typeof localStorage !== 'undefined') return localStorage;
  } catch {
    // Storage access can throw (sandboxed iframe) — degrade to memory only.
  }
  return null;
}

const keyFor = (host: string) => `${STORAGE_PREFIX}${host}`;

/** Hostname of a forecast URL — the per-host block namespace. */
export function hostForUrl(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return 'unknown';
  }
}

/** Parse a Retry-After header into whole seconds (null when unusable). */
export function parseRetryAfterSeconds(value: string | null | undefined): number | null {
  if (value == null) return null;
  const trimmed = value.trim();
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  const t = Date.parse(trimmed);
  if (Number.isFinite(t)) return Math.max(0, Math.ceil((t - Date.now()) / 1000));
  return null;
}

/** Classify the provider's reason phrase into a limit kind. */
export function limitKindFromReason(reason: string | null | undefined): LimitKind {
  const r = (reason ?? '').toLowerCase();
  if (r.includes('minut')) return 'minute'; // "Minutely"/"minute" phrases
  if (r.includes('hour')) return 'hourly';
  if (r.includes('day') || r.includes('tomorrow')) return 'daily';
  return 'unknown';
}

/** First instant of the next UTC hour. */
export function nextHourBoundary(now: number): number {
  const d = new Date(now);
  d.setUTCMinutes(0, 0, 0);
  d.setUTCHours(d.getUTCHours() + 1);
  return d.getTime();
}

/** First instant of the next UTC day. */
export function nextUtcMidnight(now: number): number {
  const d = new Date(now);
  d.setUTCHours(24, 0, 0, 0);
  return d.getTime();
}

/**
 * When the host may be called again, given a block response.
 * Retry-After wins; otherwise the reason phrase decides; unknown → 15 min.
 */
export function computeBlockedUntil(
  now: number,
  info: { retryAfterSeconds?: number | null; reason?: string | null },
): number {
  if (info.retryAfterSeconds != null && info.retryAfterSeconds > 0) {
    return now + info.retryAfterSeconds * 1000;
  }
  switch (limitKindFromReason(info.reason)) {
    case 'minute':
      return now + MINUTE_LIMIT_PAUSE_MS;
    case 'hourly':
      return nextHourBoundary(now) + HOURLY_LIMIT_SLACK_MS;
    case 'daily':
      return nextUtcMidnight(now) + DAILY_LIMIT_SLACK_MS;
    default:
      return now + UNKNOWN_BLOCK_PAUSE_MS;
  }
}

export function getBlockedUntil(host: string): number | null {
  const s = storage();
  if (s) {
    try {
      const raw = s.getItem(keyFor(host));
      if (raw != null) {
        const n = Number(raw);
        if (Number.isFinite(n)) return n;
      }
    } catch {
      // fall through to memory
    }
  }
  return _memory.get(host) ?? null;
}

export function setBlockedUntil(host: string, blockedUntil: number): void {
  _memory.set(host, blockedUntil);
  const s = storage();
  if (s) {
    try {
      s.setItem(keyFor(host), String(blockedUntil));
    } catch {
      // memory-only
    }
  }
}

export function clearBlockedUntil(host: string): void {
  _memory.delete(host);
  const s = storage();
  if (s) {
    try {
      s.removeItem(keyFor(host));
    } catch {
      // memory-only
    }
  }
}

/** Millis until the host may be called again (0 = not blocked). */
export function blockRemainingMs(host: string, now: number = Date.now()): number {
  const until = getBlockedUntil(host);
  return until == null ? 0 : Math.max(0, until - now);
}

export function isBlocked(host: string, now: number = Date.now()): boolean {
  return blockRemainingMs(host, now) > 0;
}

/** Persist a provider-reported pause for one host. */
export function recordBlock(host: string, blockedUntil: number): void {
  setBlockedUntil(host, blockedUntil);
}

/**
 * A successful request proves the host is reachable again — but only an
 * EXPIRED block entry is dropped; an active pause stays authoritative.
 */
export function clearExpiredBlock(host: string, now: number = Date.now()): void {
  const until = getBlockedUntil(host);
  if (until != null && until <= now) clearBlockedUntil(host);
}

/** Clear every block (memory + storage). Test support / logout hygiene. */
export function resetForecastBackoff(): void {
  _memory.clear();
  const s = storage();
  if (s) {
    try {
      const doomed: string[] = [];
      for (let i = 0; i < s.length; i++) {
        const k = s.key(i);
        if (k != null && k.startsWith(STORAGE_PREFIX)) doomed.push(k);
      }
      for (const k of doomed) s.removeItem(k);
    } catch {
      // memory-only
    }
  }
}
